import { addDays, daysBetween } from '../format/date.js';
import type { InvoiceOutcome, InvoiceStatus } from '../types/enums.js';
import type { InvoicePaymentOutcome, LedgerApplication } from './company-check.js';
import { round2 } from './money.js';

/**
 * Whether a posted invoice is paid - Business Central's word for it, shown under Status on
 * /fakturering/historik, /kontrakter/:id and /kunder/:id (decided 06-10-2026).
 *
 *   paid          BC's status `Paid`: nothing left to pay
 *   partly_paid   `Open`, with less left (`remainingAmount`) than the invoice's total
 *   unpaid        `Open`, with all of it left
 *
 * Null on anything but a posted invoice - a draft is not due, a cancelled one not to be paid -
 * and when BC's status is not there to read.
 *
 * **`Paid` means closed, not paid by the customer.** An invoice closed by a credit memo applied
 * to it is `Paid` in BC too (SBF1002 was closed by BFSK1000), and BC's API cannot tell the two
 * apart. BC's own Cancel sets `Canceled` instead, which is not `posted` here.
 */
export type InvoicePaymentState = 'paid' | 'partly_paid' | 'unpaid';

/** Half an øre - BC's amounts are rounded to øre, so anything below is the same amount. */
const EPSILON = 0.005;

export function invoicePaymentState(invoice: {
  status: InvoiceStatus;
  bc_status?: string | null;
  remaining_amount?: number;
  amount_incl_vat: number;
}): InvoicePaymentState | null {
  if (invoice.status !== 'posted') return null;

  const bcStatus = invoice.bc_status?.trim().toLowerCase();
  if (bcStatus === 'paid') return 'paid';
  if (bcStatus !== 'open') return null;

  const remaining = invoice.remaining_amount;
  if (remaining === undefined || !Number.isFinite(remaining)) return null;
  if (remaining <= EPSILON) return 'paid';
  return remaining < invoice.amount_incl_vat - EPSILON ? 'partly_paid' : 'unpaid';
}

/**
 * How much of an invoice the customer has **paid**, excl. VAT - its payments in the customer
 * ledger, not BC's status, which says `Paid` about an invoice a credit memo closed too.
 *
 * `outcome` is the invoice in `settleLedgerByInvoice`. Its payments - within `period`, by payment
 * date, when one is given - as a share of the invoice's ledger amount (incl. VAT, in the company's
 * currency), laid on the invoice's own amount excl. VAT. A partly paid invoice counts with its
 * paid part. Nothing for an invoice credited in full, or one the ledger does not know.
 */
export function paidExclVat(
  amountExclVat: number,
  outcome: InvoicePaymentOutcome | null | undefined,
  period?: { from: string; to: string },
): number {
  if (!outcome || !(outcome.amount > 0)) return 0;

  const paid = outcome.applications
    .filter(
      (application) =>
        application.kind === 'payment' &&
        (!period || (application.date >= period.from && application.date <= period.to)),
    )
    .reduce((sum, application) => sum + application.amount, 0);
  const share = Math.min(1, Math.max(0, paid / outcome.amount));
  return round2(amountExclVat * share);
}

/** How far back the days to pay look - a year of payments. */
export const DAYS_TO_PAY_WINDOW_DAYS = 365;

/**
 * Days to pay: the days from an invoice's posting to its payment, weighted by the amount paid,
 * over the payments of the last `windowDays` (decided 08-10-2026, in place of the DSO estimated
 * from the payment terms). Each payment counts with its own date and amount, so an invoice paid
 * in two rates counts twice.
 *
 * Null when there is no payment in the window. `exact` is false when any of the dates is the
 * oldest-first estimate - see `settleInvoices`.
 */
export function daysToPay(
  outcomes: Iterable<InvoicePaymentOutcome>,
  options: { today: string; windowDays?: number },
): { days: number | null; payments: number; exact: boolean } {
  const since = addDays(options.today, -(options.windowDays ?? DAYS_TO_PAY_WINDOW_DAYS));
  let weight = 0;
  let weighted = 0;
  let payments = 0;
  let exact = true;

  for (const outcome of outcomes) {
    for (const application of outcome.applications) {
      if (application.kind !== 'payment' || application.amount <= 0) continue;
      if (application.date < since || application.date > options.today) continue;
      weight += application.amount;
      weighted += application.amount * daysBetween(outcome.posting_date, application.date);
      payments += 1;
      if (!outcome.exact) exact = false;
    }
  }

  return {
    days: weight > 0 ? Math.round((weighted / weight) * 10) / 10 : null,
    payments,
    exact,
  };
}

/** A posted invoice's payment as the invoice lists show it - see `invoiceSettlement`. */
export interface InvoiceSettlement {
  outcome: InvoiceOutcome;
  /** The day it was paid - its last payment. Null unless `outcome` is 'paid'. */
  paid_date: string | null;
  /** The day it was closed, by a payment or a credit memo. Null while it is open. */
  closed_date: string | null;
  /** Paid by the customer, incl. VAT, in the company's currency. */
  paid_amount: number;
  credited_amount: number;
  /** The payments and credits, oldest first. */
  applications: LedgerApplication[];
  /** BC's own application dates. False: estimated, oldest invoice first. */
  exact: boolean;
}

export function invoiceSettlement(outcome: InvoicePaymentOutcome): InvoiceSettlement {
  return {
    outcome: outcome.outcome,
    paid_date: outcome.outcome === 'paid' ? outcome.settled_date : null,
    closed_date: outcome.closed_date,
    paid_amount: round2(
      Math.max(
        0,
        outcome.applications
          .filter((application) => application.kind === 'payment')
          .reduce((sum, application) => sum + application.amount, 0),
      ),
    ),
    credited_amount: outcome.credited_amount,
    applications: outcome.applications,
    exact: outcome.exact,
  };
}

/** One group in `invoiceOverview`: how many invoices, and how much money, excl. VAT. */
export interface InvoiceOverviewGroup {
  count: number;
  amount_excl_vat: number;
}

/**
 * A customer's invoices at a glance - the row under Fakturahistorik on /kunder/:id (08-10-2026).
 * Every invoice is in one group, and every amount is excl. VAT, like the table under it:
 *
 *   drafts     not posted yet - their total
 *   open       posted and not paid, not yet due - what is left to pay
 *   overdue    posted and not paid, past the due date - what is left to pay
 *   paid       paid in full - their total
 *   credited   closed by a credit memo, which BC calls Paid too - their total
 *
 * What is left is BC's remaining amount (incl. VAT) as a share of the invoice, laid on its amount
 * excl. VAT, so a partly paid invoice counts with what is left; `partly_paid` says how many are.
 * Paid and credited are told apart by the customer ledger - see `InvoiceSettlement`; without it, a
 * credited invoice counts as paid. A cancelled invoice is in no group.
 */
export interface InvoiceOverview {
  drafts: InvoiceOverviewGroup;
  open: InvoiceOverviewGroup & { partly_paid: number };
  overdue: InvoiceOverviewGroup & { partly_paid: number; oldest_days: number | null };
  paid: InvoiceOverviewGroup;
  credited: InvoiceOverviewGroup;
  /** The invoices' currency - the most common, should a customer have more than one. */
  currency: string;
}

export interface InvoiceOverviewInput {
  status: InvoiceStatus;
  bc_status?: string | null;
  remaining_amount?: number;
  amount_excl_vat: number;
  amount_incl_vat: number;
  due_date: string | null;
  currency?: string;
  settlement?: InvoiceSettlement | null;
}

export function invoiceOverview(
  invoices: readonly InvoiceOverviewInput[],
  today: string,
): InvoiceOverview {
  const empty = (): InvoiceOverviewGroup => ({ count: 0, amount_excl_vat: 0 });
  const overview: InvoiceOverview = {
    drafts: empty(),
    open: { ...empty(), partly_paid: 0 },
    overdue: { ...empty(), partly_paid: 0, oldest_days: null },
    paid: empty(),
    credited: empty(),
    currency: 'DKK',
  };
  const add = (group: InvoiceOverviewGroup, amount: number) => {
    group.count += 1;
    group.amount_excl_vat = round2(group.amount_excl_vat + amount);
  };

  const currencies = new Map<string, number>();
  for (const invoice of invoices) {
    if (invoice.status === 'cancelled') continue;
    if (invoice.currency) {
      currencies.set(invoice.currency, (currencies.get(invoice.currency) ?? 0) + 1);
    }

    if (invoice.status === 'draft') {
      add(overview.drafts, invoice.amount_excl_vat);
      continue;
    }

    const state = invoicePaymentState(invoice);
    if (state === 'paid') {
      const credited = invoice.settlement?.outcome === 'credited';
      add(credited ? overview.credited : overview.paid, invoice.amount_excl_vat);
      continue;
    }
    if (state === null) continue;

    const share =
      invoice.amount_incl_vat > 0
        ? Math.min(1, Math.max(0, (invoice.remaining_amount ?? 0) / invoice.amount_incl_vat))
        : 1;
    const left = invoice.amount_excl_vat * share;
    const due = invoice.due_date;
    const group = due && due < today ? overview.overdue : overview.open;
    add(group, left);
    if (state === 'partly_paid') group.partly_paid += 1;
    if (due && due < today) {
      const days = daysBetween(due, today);
      overview.overdue.oldest_days = Math.max(overview.overdue.oldest_days ?? 0, days);
    }
  }

  const [currency] = [...currencies.entries()].sort((a, b) => b[1] - a[1])[0] ?? [];
  if (currency) overview.currency = currency;
  return overview;
}
